import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useDeleteSeason } from "~pages/Seasons/SeasonListPage/hooks/useDeleteSeason.ts";
import { useExportSeasons } from "~pages/Seasons/SeasonListPage/hooks/useExportSeasons.ts";
import { useImportSeasons } from "~pages/Seasons/SeasonListPage/hooks/useImportSeasons.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import type { Season } from "~types/season.ts";
import { ensureDefaultOperationResponse, isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreateSeason } from "./useCreateSeason.ts";
import { useUpdateSeason } from "./useUpdateSeason.ts";

const isSeasonMutationSucceeded = (season: Season | undefined) => Boolean(season?.seasonId);

export const useSeasonService = () => {
  const { mutateAsync: createSeasonFn, isPending: isCreatePending } = useCreateSeason();
  const { mutateAsync: updateSeasonFn, isPending: isUpdatePending } = useUpdateSeason();
  const { mutateAsync: deleteSeasonFn, isPending: isDeletePending } = useDeleteSeason();
  const { mutateAsync: importSeasonsFn, isPending: isImportPending } = useImportSeasons();
  const { mutateAsync: exportSeasonsFn, isPending: isExportPending } = useExportSeasons();

  const { customerId, sport, organizationId, competitionId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove, execute } = useApiOperations(ApiOperationItems.SEASON);

  const createSeason = async (payload: Partial<Season>) => {
    const createdSeason = await create(() => createSeasonFn(payload), {
      successMessageId: "season.add.success",
      isSucceeded: isSeasonMutationSucceeded,
    });

    if (createdSeason && customerId && sport && organizationId && competitionId) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/competition/${competitionId}/seasons`, {
        state: { search: payload.nameLocal },
      });
    }

    return createdSeason;
  };

  const updateSeason = (payload: Partial<Season>) =>
    update(() => updateSeasonFn(payload), {
      successMessageId: "season.update.success",
      isSucceeded: isSeasonMutationSucceeded,
    });

  const deleteSeason = async (seasonId: string) =>
    Boolean(
      await remove(() => deleteSeasonFn(seasonId), {
        successMessageId: "season.delete.success",
        isSucceeded: isDefaultOperationSucceeded,
      }),
    );

  const importSeasons = async (fileName: string) =>
    ensureDefaultOperationResponse(
      await execute(() => importSeasonsFn(fileName), {
        successMessageId: "seasons.import.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Seasons import was not started",
    );

  const exportSeasons = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    ensureDefaultOperationResponse(
      await execute(() => exportSeasonsFn(fieldNames), {
        successMessageId: "seasons.export.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Seasons export was not started",
    );

  return {
    createSeason,
    isCreatePending,
    updateSeason,
    isUpdatePending,
    deleteSeason,
    isDeletePending,
    importSeasons,
    isImportPending,
    exportSeasons,
    isExportPending,
  };
};
